import { LocalDataKeys, SAVE_LOCAL_DATA_PREFIX } from '@libs/types';
import { eventBus } from './EventBus';
import { setLocale } from './helpers';

/**
 * Generic key-value store for small, persistent UI preferences (theme, language, …)
 * written by the `save_local_data:<key>` player action and read back by other widgets
 * via {@link getLocalDataSnapshot} / `useLocalData`, and by the network layer (e.g. to
 * report `deviceTheme` in the session object).
 *
 * Backed by `localStorage` (synchronous) so reads never have an async gap or flash. Change
 * notification goes through the shared `eventBus`, for consistency with the rest of the
 * codebase. Deliberately separate from the widget payload cache: no TTL, survives logout.
 */
const STORAGE_PREFIX = 'ps3_local_data:';

const isStorageAvailable = (): boolean => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
        return false;
    }
};

const storageKey = (key: string): string => `${STORAGE_PREFIX}${key}`;

const safeParse = (raw: string | null): unknown => {
    if (raw === null) {
        return null;
    }
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

/** Event name a write broadcasts on, e.g. `save_local_data:theme`. */
export const getLocalDataEventName = (key: string): string => `${SAVE_LOCAL_DATA_PREFIX}${key}`;

/** Synchronously returns the current value for `key` (or `null`) from `localStorage`. */
export const getLocalDataSnapshot = <T>(key: LocalDataKeys | string): T | null => {
    if (!isStorageAvailable()) {
        return null;
    }
    return (safeParse(window.localStorage.getItem(storageKey(key))) as T) ?? null;
};

/** Persists `value` under `key` and broadcasts the change so `useLocalData` subscribers re-render. */
export const saveLocalData = (key: string, value: unknown): void => {
    if (isStorageAvailable()) {
        try {
            window.localStorage.setItem(storageKey(key), JSON.stringify(value));
        } catch (e) {
            console.warn(`Failed to persist local data '${key}':`, e);
        }
    }
    eventBus.emitGeneral(getLocalDataEventName(key), value);
};

/**
 * Per-key runtime side effects for keys that need more than persistence to take effect
 * immediately. `theme` is intentionally absent: the theme provider reacts to the stored
 * value via `useLocalData`. `lang`, by contrast, must also update the `user_lang` cookie.
 */
const localDataSideEffects: Record<string, (value: unknown) => void> = {
    lang: (value) => setLocale(value as string),
};

/**
 * Single entry point for the `save_local_data:<key>` mechanism: persists the value,
 * broadcasts the change, and applies any per-key runtime side effect. Used both by the
 * player-action dispatcher and by the `changeLanguage` alias.
 */
export const applyLocalData = (key: string, value: unknown): void => {
    saveLocalData(key, value);
    localDataSideEffects[key]?.(value);
};
